import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { ImageModule } from '@df/module-image/image.module';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { HideUpComponent } from '@df/ui/hide-up/hide-up.component';
import { ToggleModule } from '@df/ui/toggle/toggle.module';
import { ToggleService } from '@df/ui/toggle/toggle.service';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { BehaviorSubject } from 'rxjs';
import { ButtonStyleComponent, buttonStylesOptions, TButtonStyles } from '../../common/buttons/button-style/button-style.component';
import { NTextBox, TextBoxComponent } from '../../common/text-box/text-box.component';
import { iconsOptions } from '../../styles/cms.config';

let uid = 0;

export namespace NCmsAcoustics101Component {
    export const ID = 'acoustics-101';

    export interface IData extends NICmsContentComponent.IData, NTextBox.IData {
        bgColor: 'bg-col-52' | 'bg-col-51';
        items: IItem[];
    }

    interface IItem {
        icon: string;
        title: string;
        subtitle: string;
        buttonStyle: TButtonStyles;
        buttonLink?: string;
        buttonCopy?: string;
    }
}

@ICmsComponent<CmsAcoustics101Component>({
    id: NCmsAcoustics101Component.ID,
    name: 'Acoustics 101',
    tags: ['Accordion', 'Acoustics 101'],
    fields: [
        <NICmsContentField.IChoiceField>{
            key: 'bgColor',
            type: NICmsContentField.EFieldType.choice,
            title: 'Background Color',
            previewMode: 'color',
            options: [
                { key: 'bg-col-52', title: 'Blue' },
                { key: 'bg-col-51', title: 'Grey' }
            ],
            default: 'bg-col-52'
        },
        {
            key: 'headerLabel',
            type: NICmsContentField.EFieldType.label,
            title: 'Header'
        },
        ...NTextBox.getFields(false, false),
        {
            key: 'accordionLabel',
            type: NICmsContentField.EFieldType.label,
            title: 'Accordion'
        },
        <NICmsContentField.IRepeaterField>{
            key: 'items',
            type: NICmsContentField.EFieldType.repeater,
            title: 'Items',
            itemName: 'Items',
            itemNameField: 'title',
            required: true,
            fields: [
                <NICmsContentField.IChoiceField>{
                    key: 'icon',
                    type: NICmsContentField.EFieldType.choice,
                    title: 'Icon',
                    options: iconsOptions,
                    previewMode: 'icon'
                },
                {
                    key: 'title',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Title',
                    required: true,
                    default: 'Title text'
                },
                {
                    key: 'subtitle',
                    type: NICmsContentField.EFieldType.textarea,
                    title: 'Subtitle',
                    required: true,
                    default: 'Subtitle text'
                },
                {
                    key: 'buttonStyle',
                    type: NICmsContentField.EFieldType.choice,
                    title: 'Button style',
                    options: buttonStylesOptions,
                    default: 'button'
                },
                {
                    key: 'buttonLink',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Button link',
                    default: '/'
                },
                {
                    key: 'buttonCopy',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Button copy',
                    default: 'Learn more'
                }
            ]
        }
    ]
})
@Component({
    standalone: true,
    imports: [TextBoxComponent, ImageModule, ICmsClientModule, HideUpComponent, ToggleModule, ButtonStyleComponent, HrefComponent],
    selector: 'cms-acoustics-101',
    templateUrl: './cms-acoustics-101.html',
    styleUrl: 'cms-acoustics-101.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CmsAcoustics101Component extends UiComponent implements NICmsContentComponent.IComponent {
    @ICmsContent({
        type: 'component'
    })
    component!: NCmsAcoustics101Component.IData;

    private toggleService = inject(ToggleService);

    readonly uid = `cms-acoustics-101-${uid++}`;

    protected getState(index: number): BehaviorSubject<boolean> {
        return this.toggleService.getState([this.uid, index]);
    }

    protected toggle(index: number): void {
        this.toggleService.toggle([this.uid, index]);
    }
}
